// components/AbsenCard.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import LocationMap from './LocationMap';
import LupaAbsenModal from './LupaAbsenModal';
import Swal from "sweetalert2";

const AbsenCard = React.memo(({ items, periode }) => {
  const dataKaryawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
  const isFreelance = String(dataKaryawan.status || '').trim().toLowerCase() === 'freelance';

  // === VALIDASI TANGGAL ===
  let dateSource = items[0].tanggal || items[0].tgl_ijin || '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateSource)) {
    const match = dateSource.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (match) {
      dateSource = `${match[3]}-${match[2]}-${match[1]}`;
    } else if (items[0].tgl_ijin && /^\d{4}-\d{2}-\d{2}$/.test(items[0].tgl_ijin)) {
      dateSource = items[0].tgl_ijin;
    }
  }

  const dateObj = new Date(`${dateSource}T00:00:00`);

  const formatDateForDisplay = (dateStr, format = 'full') => {
    const date = new Date(dateStr + 'T00:00:00');
    if (format === 'monthYear') {
      return date.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
    }
    return date.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const monthYear = formatDateForDisplay(dateSource, 'monthYear');

  // === DATA MASTER ===
  const jenisIjinList = JSON.parse(localStorage.getItem('jenis_ijin_list') || '[]');
  const getJenisIjinNama = (id) => {
    const found = jenisIjinList.find(j => j.id === parseInt(id));
    const nama = found ? found.nama : 'Ijin Biasa';

    // Jika nama adalah 'Izin' ATAU id adalah 5 → kembalikan 'Mendesak'
    if (parseInt(id) === 5 || parseInt(id) === 6) {
      return 'Mendesak';
    } else if (parseInt(id) === 14) {
      return 'Setengah Hari';
    }

    return nama;
  };

  // === DATA MASTER ===
  const penggantiList = JSON.parse(localStorage.getItem('karyawan_pengganti_list') || '[]');

  const getPenggantiNama = (id) => {
    const found = penggantiList.find(j => j.id === parseInt(id));
    return found ? found.nama : 'Ijin Biasa';
  };

  // === TIPE ABSEN ===
  const absenId = parseInt(items[0].absen_id);
  const isBerangkat = absenId === 18;
  const isLapangan = isBerangkat && (items[0].log === "lapangan" || items[0].log === "gps error");
  const isLibur = absenId === 7;
  const isDinas = absenId === 11;
  const isPiket = absenId === 8;
  const isCutiBersama = items.some(item => parseInt(item.absen_id) === 26);
  const isLiburLebaran = items.some(item => parseInt(item.absen_id) === 25);
  const isIjin = ![18, 7, 11, 8, 25, 26].includes(absenId);
  const isToleransi = items[0].uang_makan;

  // === STATUS ABSEN ===
  const hasMasuk = items[0].jam_masuk && items[0].jam_masuk !== '-' && items[0].jam_masuk !== '00:00:00';
  const hasPulang = items[0].jam_pulang && items[0].jam_pulang !== '-' && items[0].jam_pulang !== '00:00:00';
  const isFull = hasMasuk && hasPulang;

  const leftLocationMonitor = (() => {
    try {
      const monitors = JSON.parse(localStorage.getItem('absen_location_monitor') || '[]');
      if (!Array.isArray(monitors)) return null;

      return monitors.find(monitor => {
        const tanggalMatch = monitor.tanggal === items[0].tanggal;
        const shiftMatch = !monitor.shift_id || !items[0].shift_id || String(monitor.shift_id) === String(items[0].shift_id);
        const masuk = items[0].jam_masuk || items[0].masuk || '';
        const waktuMatch = !monitor.waktu || !masuk || String(monitor.waktu).substring(0, 5) === String(masuk).substring(0, 5);
        return monitor.status === 'left_location' && tanggalMatch && shiftMatch && waktuMatch;
      });
    } catch {
      return null;
    }
  })();

  const isLeftLocationViolation =
    items[0].log === 'keluar_lokasi' ||
    items[0].status_validasi === 'perlu_verifikasi' ||
    Boolean(leftLocationMonitor);

  const telat = isLeftLocationViolation ? Math.max(parseInt(items[0].telat) || 0, 11) : (parseInt(items[0].telat) || 0);
  const bolos = parseInt(items[0].bolos) || 0;
  let uangMakan = (isFull && telat === 0 && bolos === 0) || items[0].uang_makan === 12500 ? 12500 : 0;

  if (isCutiBersama || isLiburLebaran || isLeftLocationViolation) {
    uangMakan = 0;
  }
  const log = items[0].log;
  const leftLocationDurationLabel = leftLocationMonitor?.duration_label || '1 jam';
  const leftLocationNote =
    items[0].catatan_validasi ||
    (leftLocationMonitor?.left_distance
      ? `Absen masuk meninggalkan lokasi sebelum ${leftLocationDurationLabel}. Jarak terdeteksi ${leftLocationMonitor.left_distance} meter dari ${leftLocationMonitor.office_name}.`
      : `Absen masuk meninggalkan lokasi sebelum ${leftLocationDurationLabel}.`);

  // === LOKASI & FOTO ===
  const lokasiRaw = items[0].lokasi || '';
  const fotoUrl = items[0].nama
    ? `https://jne-cilacap.com/slip/images/images/${items[0].nama}`
    : null;

  // Parse waktu dari nama file foto
  const waktu = items[0].nama || '';
  const angka = waktu.split('-').pop().replace('.jpg', '');   // contoh: "20251202064337"

  const HH = angka.slice(-6, -4);
  const MM = angka.slice(-4, -2);
  const SS = angka.slice(-2);

  const jamFormat = `${HH}:${MM}:${SS}`;

  // Helper aman: ubah "HH:MM:SS" menjadi total detik, jika null → 0
  const toSeconds = (t) => {
    if (!t || typeof t !== 'string' || !t.includes(':')) return null;
    const [h, m, s] = t.split(':').map(Number);
    return h * 3600 + m * 60 + s;
  };

  // Pastikan data ada
  const fotoTime = toSeconds(jamFormat);
  const masukTime = toSeconds(items[0].jam_masuk);
  const pulangTime = toSeconds(items[0].jam_pulang);

  let statusAbsen = "Masuk";

  // Jika jam masuk atau pulang kosong → fallback
  if (fotoTime === null || (masukTime === null && pulangTime === null)) {
    statusAbsen = "Masuk"; // fallback aman
    console.log("Waktu tidak lengkap, fallback ke Masuk");
  } else {
    // Hitung selisih jika ada
    const diffMasuk = masukTime !== null ? Math.abs(fotoTime - masukTime) : Infinity;
    const diffPulang = pulangTime !== null ? Math.abs(fotoTime - pulangTime) : Infinity;

    statusAbsen = diffPulang < diffMasuk ? "Pulang" : "Masuk";
  }

  // Parse koordinat
  const [latStr, lngStr] = lokasiRaw.split(', ');
  const lat = parseFloat(latStr);
  const lng = parseFloat(lngStr);
  const isValidCoord = !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;

  // === REVERSE GEOCODING: Koordinat → Alamat ===
  const [alamat, setAlamat] = useState('Memuat alamat...');
  const [isLoadingAlamat, setIsLoadingAlamat] = useState(false);

  useEffect(() => {
    if (!isLapangan || !isValidCoord) return;

    const cacheKey = `geocode_${lat}_${lng}`;
    const cached = localStorage.getItem(cacheKey);

    if (cached) {
      setAlamat(cached);
      return;
    }

    setIsLoadingAlamat(true);
    setAlamat('Memuat alamat...');

    // Gunakan Nominatim (OpenStreetMap) - GRATIS
    fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&zoom=18`)
      .then(res => res.json())
      .then(data => {
        let displayName = data.display_name || '';
        // Ambil bagian penting: jalan, kelurahan, kota
        const parts = [];
        if (data.address?.road) parts.push(data.address.road);
        if (data.address?.suburb) parts.push(data.address.suburb);
        if (data.address?.city || data.address?.town) parts.push(data.address.city || data.address.town);
        const shortAddress = parts.join(', ') || displayName.split(', ').slice(0, 3).join(', ');

        const finalAddress = shortAddress || 'Alamat tidak ditemukan';
        setAlamat(finalAddress);
        localStorage.setItem(cacheKey, finalAddress); // Cache
      })
      .catch(() => {
        setAlamat(`Lokasi: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
      })
      .finally(() => {
        setIsLoadingAlamat(false);
      });
  }, [isLapangan, isValidCoord, lat, lng]);

  // CEK KOSONG MASUK / PULANG
  const tidakAbsenMasuk =
    !items[0].jam_masuk ||
    items[0].jam_masuk === "00:00" ||
    items[0].jam_masuk === "";

  const tidakAbsenPulang =
    !items[0].jam_pulang ||
    items[0].jam_pulang === "00:00" ||
    items[0].jam_pulang === "";

  // FUNSI VALIDASI TANGGAL
  const isValidDate = (d) => d instanceof Date && !isNaN(d);

  // CEK APAKAH H+1
  const isHPlusOne = (() => {
    const tanggal = items?.[0]?.tanggal;
    if (!tanggal) return false;

    const absenDate = new Date(tanggal);
    if (!isValidDate(absenDate)) return false;

    const today = new Date();

    const nextDay = new Date(absenDate);
    nextDay.setDate(absenDate.getDate() + 1);

    const toYMD = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    return toYMD(today) === toYMD(nextDay);
  })();

  function normalizeTime(value) {
    if (!value) return null;

    // Jika cuma HH:mm → tambahin :00
    if (value.length === 5) {
      return `${value}:00`;
    }

    // Sudah HH:mm:ss
    return value;
  }

  const submitLupaAbsen = async (type) => {
    const dataKaryawan = JSON.parse(localStorage.getItem("data_karyawan") || "{}");

    if (!items?.[0]?.id) {
      alert("Data absen tidak ditemukan");
      return false;
    }

    const payload = {
      id: items[0].id,
      karyawan_id: dataKaryawan.id, // PASTIKAN INI ADA
      jam_masuk: normalizeTime(jamMasuk),
      jam_pulang: normalizeTime(jamPulang),
      log: type,
    };

    try {
      const res = await fetch(
        "https://jne-cilacap.com/slip/public/api/lupa",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const result = await res.json();

      // ✅ SUCCESS SWAL
      await Swal.fire({
        icon: "success",
        title: "Berhasil 🎉",
        text: "Laporan lupa absen berhasil dikirim",
        confirmButtonText: "OK",
        confirmButtonColor: "#4f7cff",
      });

      if (!res.ok) {
        console.error(result);
        alert(result.message || "Gagal mengirim laporan");

        Swal.fire({
          icon: "error",
          title: "Gagal ❌",
          text: err.message || "Terjadi kesalahan",
          confirmButtonColor: "#ef4444",
        });

        return false;
      }

      // UPDATE LOCAL STORAGE
      const list = JSON.parse(localStorage.getItem("absen_server") || "[]");
      const updated = list.map(item =>
        item.id === payload.id
          ? { ...item, log: type, status: "revisi" }
          : item
      );
      localStorage.setItem("absen_server", JSON.stringify(updated));

      return true;

    } catch (err) {
      console.error("Network error", err);
      alert("Koneksi bermasalah");
      return false;
    }
  };

  const [showLupaModal, setShowLupaModal] = useState(false);
  const [lupaType, setLupaType] = useState(null);
  const [jamMasuk, setJamMasuk] = useState("");
  const [jamPulang, setJamPulang] = useState("");
  const [saving, setSaving] = useState(false);


  const handleLupaMasuk = () => {
    setLupaType("lupa_masuk");
    setShowLupaModal(true);
  };

  const handleLupaPulang = () => {
    setLupaType("lupa_pulang");
    setShowLupaModal(true);
  };


  const handleSubmitLupa = async () => {
    setSaving(true);

    const ok = await submitLupaAbsen(lupaType);
    if (!ok) {
      setSaving(false);
      return;
    }

    setSaving(false);
    setShowLupaModal(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: "#fff",
        border: "1px solid #edf0f5",
        borderRadius: 16,
        padding: 16,
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        fontSize: 14,
        marginBottom: 16,
      }}
    >
      {/* HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12, background: "#eef4ff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, color: "#003087", fontWeight: 700
          }}>
            {dateObj.getDate()}
          </div>
          <div style={{ marginLeft: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#2b2d42" }}>
              {dateObj.toLocaleDateString('id-ID', { weekday: 'long' })}
            </div>
            <div style={{ fontSize: 13, color: "#6c757d" }}>{monthYear}</div>
          </div>
        </div>
        <span style={{ color: "#003087", fontWeight: 600, fontSize: 14 }}>
          {
            (isDinas && 'Dinas') ||
            (isCutiBersama && 'Cuti Bersama') ||
            (isLiburLebaran && 'Libur Lebaran') ||
            (isIjin && 'Ijin') ||
            (isLibur && 'Libur') ||
            (isPiket && 'Piket') ||
            items[0].jadwal
          }
        </span>
      </div>

      {/* TIPE KHUSUS */}
      {isLibur && (
        <div style={{ textAlign: 'center', padding: 12, background: '#fdeaea', borderRadius: 12, marginBottom: 12 }}>
          <span style={{ fontWeight: 700, color: '#dc2626' }}>Hari Libur Rutin</span>
        </div>
      )}

      {isCutiBersama && (
        <div style={{ textAlign: 'center', padding: 12, background: '#fff7ed', borderRadius: 12, marginBottom: 12 }}>
          <span style={{ fontWeight: 700, color: '#ea580c' }}>Idul Fitri</span>
        </div>
      )}

      {isLiburLebaran && (
        <div style={{ textAlign: 'center', padding: 12, background: '#faf5ff', borderRadius: 12, marginBottom: 12 }}>
          <span style={{ fontWeight: 700, color: '#9333ea' }}>Idul Fitri</span>
        </div>
      )}

      {isDinas && (
        <div style={{ background: '#fff8e1', padding: 12, borderRadius: 12, marginBottom: 12, fontSize: 13 }}>
          {/* <strong>Dinas Luar</strong><br /> */}
          <span style={{ color: '#92400e' }}>Dalam Rangka: {items[0].nama || 'Tidak ada keterangan'}</span>
        </div>
      )}

      {isIjin && (
        <div style={{ background: '#fdeaea', padding: 12, borderRadius: 12, marginBottom: 12, fontSize: 13 }}>
          <span>Jenis Ijin : <strong>{getJenisIjinNama(items[0].absen_id) === 'Izin' ? 'Mendesak' : getJenisIjinNama(items[0].absen_id)}</strong></span><br />
          <span>Keterangan : <strong>{items[0].nama || 'Tidak ada'}</strong></span><br />
          <span>Pengganti : <strong>{getPenggantiNama(items[0].jam_masuk) || 'Tidak ada'}</strong></span><br />
          <span style={{ fontStyle: 'italic', color: '#666' }}>{items[0].extra?.keterangan || ''}</span>
        </div>
      )}

      {isPiket && (
        <div style={{ background: '#b9c1f8', padding: 12, borderRadius: 12, marginBottom: 12, fontSize: 13 }}>
          {/* <strong>Dinas Luar</strong><br /> */}
          <span style={{ color: '#92400e' }}>Dalam Rangka: {items[0].nama || 'Tidak ada keterangan'}</span>
        </div>
      )}

      {/* ABSEN LAPANGAN */}
      {isLapangan && (
        <div style={{
          background: "#f0f9ff",
          padding: 12,
          borderRadius: 12,
          marginBottom: 12,
          position: "relative",
          overflow: "hidden",
        }}>
          <div style={{ fontWeight: 700, color: "#003087", marginBottom: 8, textAlign: 'center' }}>
            {absenId === 18 && items[0].log === "gps error" ? `Absen ${statusAbsen} GPS Error` : `Absen ${statusAbsen} Lapangan`}
          </div>

          {/* FOTO + MAP OVERLAY */}
          <div style={{
            position: "relative",
            borderRadius: 8,
            overflow: "hidden",
            marginTop: 12,
          }}>
            {fotoUrl ? (
              <img
                src={fotoUrl}
                alt="Bukti Absen"
                style={{ width: "100%", height: '370px', display: "block" }}
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
              />
            ) : null}
            <div style={{ display: fotoUrl ? 'none' : 'block', textAlign: 'center', marginBottom: 36, padding: 16, color: '#999' }}>
              Sudah Di Validasi Atasan / HC
            </div>

            {isValidCoord && (
              <div style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "100%",
                height: "65px",
                boxShadow: "0 -10px 20px rgba(0,0,0,0.3) inset",
                zIndex: 2,
              }}>
                <LocationMap lat={lat} lng={lng} address={alamat} />
              </div>
            )}
          </div>
          {/* ALAMAT DARI KOORDINAT */}
          <div style={{ marginTop: 8 }}>
            <p style={{
              fontSize: 13,
              margin: "4px 0",
              color: "#0c4a6e",
              fontWeight: 300,
              fontStyle: 'italic'
            }}>
              {isLoadingAlamat ? 'Memuat alamat...' : alamat}
            </p>

          </div>
        </div>
      )}

      {/* STATUS, JAM, UANG MAKAN */}
      {isBerangkat && (() => {
        let statusText = "Belum Lengkap";
        let color = "#d32f2f";
        if (telat > 0) statusText = `Anda Telat ${telat} menit`;
        else if (bolos > 0) statusText = "Anda Bolos";
        else if (isFull) { statusText = "Absen Lengkap"; color = "#2e7d32"; }

        return (
          <div style={{ textAlign: 'center', marginBottom: 10, fontSize: 13.5, fontWeight: 600, color }}>
            {statusText}
          </div>
        );
      })()}

      {isBerangkat && isLeftLocationViolation && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          color: '#b91c1c',
          padding: 12,
          borderRadius: 12,
          marginBottom: 10,
          fontSize: 13,
          fontWeight: 700,
          lineHeight: 1.45,
          textAlign: 'center',
        }}>
          Absen masuk dihitung telat 11 menit karena meninggalkan lokasi sebelum {leftLocationDurationLabel}. Uang makan menjadi Rp 0.
          <div style={{ marginTop: 4, fontWeight: 600 }}>
            {leftLocationNote}
          </div>
        </div>
      )}

      {isBerangkat && (
        <>
          <div style={{ background: "#f8fafc", padding: 10, borderRadius: 12, display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#6c757d" }}>Absen Masuk</div>
              <div style={{ fontWeight: 700, color: hasMasuk ? "#2e7d32" : "#d32f2f" }}>
                {
                  (!tidakAbsenMasuk)
                    ? items[0].jam_masuk
                    : (
                      isHPlusOne ? (
                        <span
                          onClick={handleLupaMasuk}
                          style={{
                            color: "#d32f2f",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: 700
                          }}
                        >
                          Lupa Absen
                        </span>
                      ) : "00:00"
                    )
                }
              </div>
            </div>
            <div style={{ width: 1, background: "#e2e8f0", margin: "0 12px" }}></div>
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#6c757d" }}>Absen Pulang</div>
              <div style={{ fontWeight: 700, color: hasPulang ? "#2e7d32" : "#d32f2f" }}>
                {
                  (!tidakAbsenPulang)
                    ? items[0].jam_pulang
                    : (
                      isHPlusOne ? (
                        <span
                          onClick={handleLupaPulang}
                          style={{
                            color: "#d32f2f",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: 700
                          }}
                        >
                          Lupa Absen
                        </span>
                      ) : "00:00"
                    )
                }
              </div>
            </div>
          </div>

          <div style={{ background: "#f8fafc", padding: 10, borderRadius: 12, display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#6c757d" }}>Telat</div>
              <div style={{ fontWeight: 700, color: telat > 0 ? "#d32f2f" : "#2e7d32" }}>
                {telat > 0 ? `${telat} menit` : "0"}
              </div>
            </div>
            <div style={{ width: 1, background: "#e2e8f0", margin: "0 12px" }}></div>
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#6c757d" }}>Bolos</div>
              <div style={{ fontWeight: 700, color: bolos > 0 ? "#d32f2f" : "#2e7d32" }}>
                {bolos > 0 ? `${bolos} menit` : "0"}
              </div>
            </div>
          </div>
        </>
      )}

      {isToleransi && !isLibur && (
        <>
          <div style={{
            background: uangMakan > 0 ? "#e8f7ec" : "#fdeaea",
            padding: 10, borderRadius: 10, textAlign: "center", marginBottom: 10,
            fontWeight: 700, color: uangMakan > 0 ? "#2e7d32" : "#d32f2f",
          }}>
            {log}
          </div>
        </>
      )}

      {!isFreelance && (
        <div style={{
          background: uangMakan > 0 ? "#e8f7ec" : "#fdeaea",
          padding: 10, borderRadius: 10, textAlign: "center",
          fontWeight: 700, color: uangMakan > 0 ? "#2e7d32" : "#d32f2f",
        }}>
          Uang Makan: Rp {uangMakan.toLocaleString("id-ID")}
        </div>
      )}
      <LupaAbsenModal
        open={showLupaModal}
        onClose={() => setShowLupaModal(false)}
        onSubmit={handleSubmitLupa}
        lupaType={lupaType}
        items={items}
        jamMasuk={jamMasuk}
        setJamMasuk={setJamMasuk}
        jamPulang={jamPulang}
        setJamPulang={setJamPulang}
        saving={saving}
        dataKaryawan={dataKaryawan}
      />

    </motion.div>
  );
});

export default AbsenCard;
